import { colors } from '@beelday/common'
import styled from '@emotion/styled'
import { FC } from 'react'

/**
 * A ring that grows with how much of `max` is `value`. `ui.CircleProgress`
 * has a fixed pixel size and a 14px figure, which is a thumbnail on a
 * projector; this one takes the room the pane gives it, and is a progress bar
 * for assistive tech.
 */
const Ring = styled.div`
	--ring-size: clamp(180px, min(22vw, 38vh), 400px);
	flex: none;
	height: var(--ring-size);
	position: relative;
	width: var(--ring-size);
`

const Circle = styled.svg`
	height: 100%;
	transform: rotate(-90deg);
	width: 100%;

	circle {
		fill: none;
		stroke-width: 9;
	}
`

const Track = styled.circle`
	stroke: ${colors.brightGray};
`

const Value = styled.circle`
	stroke: ${colors.greenishCyan};
	stroke-linecap: round;
	transition: stroke-dashoffset 0.6s ease-in-out;

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`

const Figure = styled.div`
	align-items: center;
	color: ${colors.blackish};
	display: flex;
	font-size: calc(var(--ring-size) * 0.26);
	font-weight: 700;
	inset: 0;
	justify-content: center;
	position: absolute;
	white-space: nowrap;
`

type Props = {
	value: number
	max: number
	/** What the ring counts, in words: "3 of 5 finished". */
	label: string
}

const CountRing: FC<Props> = ({ value, max, label }) => {
	const percent = max > 0 ? Math.min(100, (100 * value) / max) : 0
	return (
		<Ring
			role="progressbar"
			aria-label={label}
			aria-valuemin={0}
			aria-valuemax={max}
			aria-valuenow={value}
		>
			<Circle viewBox="0 0 100 100" aria-hidden="true">
				<Track cx="50" cy="50" r="45" />
				{percent > 0 ? (
					<Value
						cx="50"
						cy="50"
						r="45"
						pathLength={100}
						strokeDasharray="100"
						strokeDashoffset={100 - percent}
					/>
				) : null}
			</Circle>
			<Figure aria-hidden="true">
				{value}/{max}
			</Figure>
		</Ring>
	)
}

export default CountRing
